import * as React from "react";
import { Input, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputRef } from "antd/lib/input/";
import { LoadingOutlined } from "@ant-design/icons";
import { hasBlacklistedFilenameChars, getBlacklistedFilenameCharsMessage } from "utils/inputValidation";
import i18nConfig, { I18N_NS } from "Config/i18n";

interface EditableDetailItemProps {
  label: string;
  value: string;
  render?: (value: string) => React.ReactNode;
  editable: boolean;
  onSave?: (newValue: string) => Promise<void>;
  validateFilename?: boolean;
}

interface EditableDetailItemState {
  isEditing: boolean;
  loading: boolean;
  validationError: string;
}

export class EditableDetailItem extends React.Component<EditableDetailItemProps, EditableDetailItemState> {
  private inputRef: React.RefObject<InputRef>;
  constructor(props: EditableDetailItemProps) {
    super(props);
    this.state = {
      isEditing: false,
      loading: false,
      validationError: "",
    };
    this.inputRef = React.createRef();
  }

  handleInputChange = () => {
    if (!this.props.validateFilename) return;
    const value = this.inputRef.current?.input?.value || "";
    if (hasBlacklistedFilenameChars(value)) {
      this.setState({
        validationError: getBlacklistedFilenameCharsMessage(i18nConfig.getFixedT(null, I18N_NS.PROJECT)),
      });
    } else {
      this.setState({ validationError: "" });
    }
  };

  handleSave = async () => {
    const newValue = this.inputRef.current.input.value;
    if (this.props.validateFilename && hasBlacklistedFilenameChars(newValue)) {
      return;
    }
    try {
      this.setState({ loading: true });
      await this.props.onSave(newValue);
    } catch (error) {
      throw error;
    } finally {
      this.setState({ loading: false, isEditing: false, validationError: "" });
    }
  };

  toggleEdit = () => {
    this.setState((prev) => ({ isEditing: !prev.isEditing, validationError: "" }));
  };

  render() {
    const { value, label, editable, render } = this.props;
    const { isEditing, loading, validationError } = this.state;
    const suffix = loading ? <LoadingOutlined /> : undefined;

    return (
      <div className="file-details--details">
        <div className="file-details--title">{label}:</div>
        <div className="file-details--value">
          {isEditing ? (
            <>
              <Input
                suffix={suffix}
                ref={this.inputRef}
                size="small"
                defaultValue={value}
                onChange={this.handleInputChange}
                status={validationError ? "error" : undefined}
              />
              {validationError && <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 2 }}>{validationError}</div>}
            </>
          ) : render ? (
            render(value)
          ) : (
            value
          )}
        </div>
        {editable ? (
          isEditing ? (
            <div className="file-details--icon">
              <button type="button" aria-label="Cancel" onClick={this.toggleEdit}>
                <FontAwesomeIcon size="lg" fixedWidth={true} icon={["far", "times"]} />
              </button>
              <Tooltip title={validationError || undefined}>
                <button
                  type="button"
                  aria-label="Save"
                  disabled={!!validationError}
                  onClick={this.handleSave}
                  style={validationError ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  <FontAwesomeIcon size="lg" fixedWidth={true} icon={["far", "check"]} />
                </button>
              </Tooltip>
            </div>
          ) : (
            <div className="file-details--icon">
              <button type="button" aria-label="Edit" onClick={this.toggleEdit}>
                <FontAwesomeIcon size="lg" fixedWidth={true} icon={["far", "edit"]} />
              </button>
            </div>
          )
        ) : null}
      </div>
    );
  }
}
